import { ArrowRight } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';

import { GarmentMockup } from '@/components/garment-mockup';
import { EmptyState } from '@/components/ui';
import { formatCents } from '@/lib/money';
import { useSuspenseQuery } from '@tanstack/react-query';
import { customQuery } from '@/queries';

export default function CustomPage() {
  const { products, methods } = useSuspenseQuery(customQuery).data;

  const byCategory = products.reduce<Record<string, typeof products>>((acc, p) => {
    (acc[p.category] ??= []).push(p);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-[1180px] px-[30px] py-12">
      <header className="max-w-[62ch]">
        <h1 className="text-[32px] font-extrabold tracking-[-0.03em] sm:text-[42px]">
          Order your own design
        </h1>
        <p className="mt-3 text-[17px] leading-relaxed text-muted">
          Pick a garment to open the studio. Place your artwork on it, choose how it gets
          decorated, and the estimate updates as you go.
        </p>
      </header>

      {/* Method reference, so the blank choice is made with the rates visible.
          A heading with nothing under it reads as a broken page, and every
          method is switched off until the rate card arrives (Q-13), so the
          empty case says which it is. R-140. */}
      <section className="mt-10" aria-labelledby="methods-heading">
        <h2 id="methods-heading" className="text-lg font-bold tracking-[-0.02em]">
          How we decorate
        </h2>
        {methods.length === 0 && (
          <div className="mt-4">
            <EmptyState
              title="Decoration prices are not set yet"
              body="Call the studio and we will quote your job by hand while the price list is finished."
            />
          </div>
        )}
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {methods.map((m) => (
            <div key={m.key} className="rounded-[10px] border border-line bg-panel p-4">
              <p className="text-[15px] font-bold">{m.name}</p>
              <p className="mt-1 text-sm text-muted">{m.turnaround}</p>
              <p className="mt-2 text-sm">
                <span className="text-muted">Minimum</span>{' '}
                <span className="font-semibold">
                  {m.moq} {m.moq === 1 ? 'unit' : 'units'}
                </span>
              </p>
              <p className="text-sm">
                <span className="text-muted">Colours</span>{' '}
                <span className="font-semibold">
                  {m.max_colours == null ? 'Full colour' : `up to ${m.max_colours}`}
                </span>
              </p>
            </div>
          ))}
        </div>
      </section>

      {products.length === 0 && (
        <section className="mt-12">
          <EmptyState
            title="No garments to choose from yet"
            body="The garment range is being loaded. Call the studio and we will start your order over the phone."
          />
        </section>
      )}

      {Object.entries(byCategory).map(([category, items]) => (
        <section key={category} className="mt-12" aria-labelledby={`cat-${category}`}>
          <h2
            id={`cat-${category}`}
            // `category` is the stored key, and it is lowercase: the heading
            // read "tops" under "How we decorate" and "Order your own design".
            // Sentence case here rather than `capitalize`, which would title
            // case a two-word category into "Fishing Wear". R-261.
            className="mb-4 text-lg font-bold tracking-[-0.02em] first-letter:uppercase"
          >
            {category}
          </h2>
          <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
            {items.map((p) => (
              <Link
                key={p.id}
                to="/custom/studio/$productId" params={{ productId: p.id }}
                className="reveal group flex flex-col overflow-hidden rounded-[10px] border border-line bg-panel transition-[transform,box-shadow] duration-200 ease-[var(--ease-out-soft)] hover:-translate-y-0.5 hover:shadow-[var(--shadow-panel)]"
              >
                <div className="bg-raised p-3">
                  <GarmentMockup
                    type={p.type}
                    colorHex={p.colors[0]!.hex}
                    label={`${p.name} blank in ${p.colors[0]!.name}`}
                    className="aspect-square w-full"
                  />
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="text-[15px] font-bold leading-snug tracking-[-0.01em]">
                    {p.name}
                  </h3>
                  <p className="mt-0.5 text-sm text-muted">
                    {p.colors.length} colours &middot; {p.sizes.length}{' '}
                    {p.sizes.length === 1 ? 'size' : 'sizes'}
                  </p>
                  <p className="mt-auto pt-3 text-[15px]">
                    <span className="text-lg font-bold">
                      {formatCents(p.base_price_cents)}
                    </span>{' '}
                    <span className="text-muted">blank</span>
                  </p>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-ink">
                    Open the studio
                    <ArrowRight
                      size={15}
                      weight="bold"
                      className="transition-transform group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
